import { useState, useCallback, useMemo, useEffect } from "react";
import { GenericField, InputProps } from "../Fields/GenericField";
import { useField } from "formik";
import { Button } from "@/components/catalyst/button";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

export type DateOfBirthInputProps = InputProps & {
  onChange?: (value: string | null) => void;
  minYear?: number;
  maxYear?: number;
};

export function DateOfBirthInput({
  onChange,
  minYear = 1900,
  maxYear = new Date().getFullYear(),
  ...props
}: DateOfBirthInputProps) {
  const [{ value }, , { setValue }] = useField<string | null>(props.name);
  const [dayOpen, setDayOpen] = useState(false);
  const [monthOpen, setMonthOpen] = useState(false);
  const [yearOpen, setYearOpen] = useState(false);

  // Store individual selections separately to allow partial selection
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  const parsedDate = useMemo(() => {
    if (!value)
      return { day: selectedDay, month: selectedMonth, year: selectedYear };
    const date = new Date(value);
    if (isNaN(date.getTime()))
      return { day: selectedDay, month: selectedMonth, year: selectedYear };
    return {
      day: date.getDate(),
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    };
  }, [value, selectedDay, selectedMonth, selectedYear]);

  // Sync state when value changes (e.g., when form loads with existing data)
  useEffect(() => {
    if (value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        setSelectedDay(date.getDate());
        setSelectedMonth(date.getMonth() + 1);
        setSelectedYear(date.getFullYear());
      }
    } else {
      // Reset individual selections when value is cleared
      setSelectedDay(null);
      setSelectedMonth(null);
      setSelectedYear(null);
    }
  }, [value]);

  const days = useMemo(() => {
    const daysInMonth =
      parsedDate.month && parsedDate.year
        ? new Date(parsedDate.year, parsedDate.month, 0).getDate()
        : 31;
    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
  }, [parsedDate.month, parsedDate.year]);

  const months = useMemo(
    () => [
      { value: 1, label: "January" },
      { value: 2, label: "February" },
      { value: 3, label: "March" },
      { value: 4, label: "April" },
      { value: 5, label: "May" },
      { value: 6, label: "June" },
      { value: 7, label: "July" },
      { value: 8, label: "August" },
      { value: 9, label: "September" },
      { value: 10, label: "October" },
      { value: 11, label: "November" },
      { value: 12, label: "December" },
    ],
    [],
  );

  const years = useMemo(() => {
    return Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i);
  }, [minYear, maxYear]);

  const updateDate = useCallback(
    async (newDay?: number, newMonth?: number, newYear?: number) => {
      // Update individual state
      if (newDay !== undefined) setSelectedDay(newDay);
      if (newMonth !== undefined) setSelectedMonth(newMonth);
      if (newYear !== undefined) setSelectedYear(newYear);

      const day = newDay ?? parsedDate.day;
      const month = newMonth ?? parsedDate.month;
      const year = newYear ?? parsedDate.year;

      // Only create a complete date if all three values are present
      if (day && month && year) {
        const dateString = `${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
        await setValue(dateString);
        onChange?.(dateString);
      } else {
        // Clear the field value if not all parts are selected, but keep individual selections
        await setValue(null);
        onChange?.(null);
      }
    },
    [parsedDate, setValue, onChange],
  );

  const formattedDate = useMemo(() => {
    if (!value) return "--";
    const date = new Date(value);
    if (isNaN(date.getTime())) return "--";
    return date.toLocaleDateString();
  }, [value]);

  return (
    <GenericField viewNode={<span>{formattedDate}</span>} {...props}>
      {() => (
        <div className="flex space-x-2">
          {/* Day Select */}
          <Popover open={dayOpen} onOpenChange={setDayOpen}>
            <PopoverTrigger asChild>
              <Button
                plain
                className="shadow-xs h-[38px] w-full min-w-0 border-gray-300 text-sm font-normal"
              >
                <span
                  className={cn("grow truncate text-left", {
                    "text-gray-400": !parsedDate.day,
                  })}
                >
                  {parsedDate.day || "Day"}
                </span>
                <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-32 p-0" align="start">
              <Command>
                <CommandList>
                  <CommandGroup>
                    {days.map((day) => (
                      <CommandItem
                        key={day}
                        value={day.toString()}
                        onSelect={async () => {
                          await updateDate(day);
                          setDayOpen(false);
                        }}
                      >
                        {day}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          <Popover open={monthOpen} onOpenChange={setMonthOpen}>
            <PopoverTrigger asChild>
              <Button
                plain
                className="shadow-xs h-[38px] w-full min-w-0 border-gray-300 text-sm font-normal"
              >
                <span
                  className={cn("grow truncate text-left", {
                    "text-gray-400": !parsedDate.month,
                  })}
                >
                  {months.find((m) => m.value === parsedDate.month)?.label ||
                    "Month"}
                </span>
                <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-40 p-0" align="start">
              <Command>
                <CommandList>
                  <CommandGroup>
                    {months.map((month) => (
                      <CommandItem
                        key={month.value}
                        value={month.label}
                        onSelect={async () => {
                          await updateDate(undefined, month.value);
                          setMonthOpen(false);
                        }}
                      >
                        {month.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <Popover open={yearOpen} onOpenChange={setYearOpen}>
            <PopoverTrigger asChild>
              <Button
                plain
                className="shadow-xs h-[38px] w-full min-w-0 border-gray-300 text-sm font-normal"
              >
                <span
                  className={cn("grow truncate text-left", {
                    "text-gray-400": !parsedDate.year,
                  })}
                >
                  {parsedDate.year || "Year"}
                </span>
                <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-28 p-0" align="start">
              <Command>
                <CommandList className="max-h-60">
                  <CommandEmpty>No year found.</CommandEmpty>
                  <CommandGroup>
                    {years.map((year) => (
                      <CommandItem
                        key={year}
                        value={year.toString()}
                        onSelect={async () => {
                          await updateDate(undefined, undefined, year);
                          setYearOpen(false);
                        }}
                      >
                        {year}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      )}
    </GenericField>
  );
}
